import { useEffect, useRef, useState } from 'react'
import ResearchShell from '../components/research/ResearchShell'
import { HistogramChart, PathsChart, SectorChips, StatTile, TornadoChart } from '../components/research/charts'
import type { McResult, McStrategy, Sector } from '../types/research'
import { runStrategyMonteCarlo } from '../lib/research-api'

function card(extra = '') {
  return `bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 ${extra}`
}

function fmtMoney(v: number): string {
  const sign = v < 0 ? '−' : ''
  const a = Math.abs(v)
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}k`
  return `${sign}$${a.toFixed(0)}`
}

const inputCls =
  'mt-1 w-full bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-2.5 py-2 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none'

export default function MonteCarloPage() {
  const [strategy, setStrategy] = useState<McStrategy>('marker-tilt')
  const [sector, setSector] = useState<Sector | null>(null)
  const [paths, setPaths] = useState(5000)
  const [horizonDays, setHorizonDays] = useState(60)
  const [volMultiplier, setVolMultiplier] = useState(1)
  const [driftAdjustPct, setDriftAdjustPct] = useState(0)
  const [seed, setSeed] = useState(42)
  const notional = 100000

  const [result, setResult] = useState<McResult | null>(null)
  const [running, setRunning] = useState(false)
  const runIdRef = useRef(0)

  async function run() {
    const id = ++runIdRef.current
    setRunning(true)
    try {
      const r = await runStrategyMonteCarlo({
        strategy,
        sector,
        paths,
        horizonDays,
        notional,
        seed,
        volMultiplier,
        driftAdjustPct,
      })
      if (id === runIdRef.current) setResult(r)
    } finally {
      if (id === runIdRef.current) setRunning(false)
    }
  }

  // One run on mount with defaults; afterwards the Run button drives it.
  useEffect(() => {
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const s = result?.stats

  return (
    <ResearchShell showMockBanner={!!result?.basisMock && !running}>
      {/* Config */}
      <div className={card('mb-6')}>
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
          Monte Carlo strategy simulation
        </h2>
        <div className="mb-4">
          <SectorChips value={sector} onChange={setSector} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <label className="block col-span-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">Strategy (from the other tools)</span>
            <select value={strategy} onChange={(e) => setStrategy(e.target.value as McStrategy)} className={inputCls}>
              <option value="marker-tilt">Marker-tilt long portfolio (Marker Lab)</option>
              <option value="convert-parity">Convertible parity capture (Convertibles)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 dark:text-slate-400">Paths</span>
            <input type="number" min={500} max={20000} step={500} value={paths} onChange={(e) => setPaths(Number(e.target.value))} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 dark:text-slate-400">Horizon (days)</span>
            <input type="number" min={5} max={252} value={horizonDays} onChange={(e) => setHorizonDays(Number(e.target.value))} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 dark:text-slate-400">Vol multiplier</span>
            <input type="number" min={0.25} max={3} step={0.25} value={volMultiplier} onChange={(e) => setVolMultiplier(Number(e.target.value))} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 dark:text-slate-400">Drift shift (%/yr)</span>
            <input type="number" min={-20} max={20} step={1} value={driftAdjustPct} onChange={(e) => setDriftAdjustPct(Number(e.target.value))} className={inputCls} />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={run}
            disabled={running}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white transition-colors"
          >
            {running ? 'Simulating…' : `Run ${paths.toLocaleString()} paths`}
          </button>
          <label className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">Seed</span>
            <input
              type="number"
              value={seed}
              onChange={(e) => setSeed(Number(e.target.value))}
              className="w-24 bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
            />
          </label>
          <button
            onClick={() => setSeed(Math.floor(Math.random() * 1e6))}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            title="Pick a new random seed (then Run)"
          >
            Reroll seed
          </button>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Notional ${notional.toLocaleString()} · seeded runs are exactly reproducible
          </span>
        </div>
      </div>

      {running && (
        <div className={card('animate-pulse')}>
          <div className="h-48 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
          <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>
      )}

      {!running && result && s && (
        <div className="space-y-6">
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
              {result.strategyLabel} · {result.config.paths.toLocaleString()} paths · {result.config.horizonDays}{' '}
              trading days · seed {result.config.seed}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatTile label="Mean P&L" value={fmtMoney(s.meanPnl)} tone={s.meanPnl >= 0 ? 'good' : 'bad'} sub={`median ${fmtMoney(s.medianPnl)}`} />
              <StatTile label="P(profit)" value={`${(s.probProfit * 100).toFixed(1)}%`} tone="accent" sub={`Sharpe ≈ ${s.sharpe.toFixed(2)} (annualized)`} />
              <StatTile label="5th – 95th percentile" value={`${fmtMoney(s.p5)} to ${fmtMoney(s.p95)}`} sub={`σ ${fmtMoney(s.stdevPnl)}`} />
              <StatTile label="VaR / CVaR (95%)" value={fmtMoney(-s.var95)} tone={s.var95 > 0 ? 'bad' : 'neutral'} sub={`CVaR ${fmtMoney(-s.cvar95)} · worst ${fmtMoney(s.worst)}`} />
            </div>
          </div>

          {/* Outcome distribution */}
          <div className={card()}>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
              Outcome distribution — P&amp;L at horizon
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Histogram of {result.config.paths.toLocaleString()} simulated outcomes with a fitted normal curve.
              Red bins lose money; dashed lines mark the 5th percentile, mean, and 95th percentile.
            </p>
            <HistogramChart
              bins={result.histogram}
              overlay={result.normalOverlay}
              markers={[
                { value: s.p5, label: `P5 ${fmtMoney(s.p5)}`, stroke: 'stroke-rose-500 dark:stroke-rose-400', fill: 'fill-rose-600 dark:fill-rose-400' },
                { value: s.meanPnl, label: `mean ${fmtMoney(s.meanPnl)}`, stroke: 'stroke-blue-500 dark:stroke-blue-400', fill: 'fill-blue-600 dark:fill-blue-400' },
                { value: s.p95, label: `P95 ${fmtMoney(s.p95)}`, stroke: 'stroke-emerald-500 dark:stroke-emerald-400', fill: 'fill-emerald-600 dark:fill-emerald-400' },
              ]}
              formatValue={fmtMoney}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Sensitivity tornado */}
            <div className={card()}>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Model sensitivity</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                Mean P&amp;L when each input is shocked ±20% (same seed, everything else held fixed). Longer bars =
                the strategy's outcome depends more on that assumption.
              </p>
              <TornadoChart
                rows={result.sensitivities.map((x) => ({ label: x.label, low: x.low, high: x.high }))}
                base={s.meanPnl}
                formatValue={fmtMoney}
              />
            </div>

            {/* Sample paths */}
            <div className={card()}>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Sample paths</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                30 simulated portfolio-value paths over the holding horizon (dashed line = starting notional).
              </p>
              <PathsChart paths={result.samplePaths} startValue={result.config.notional} formatValue={fmtMoney} />
            </div>
          </div>

          {/* Positions */}
          <div className={card()}>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Simulated positions</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 pr-3 font-medium">Symbol</th>
                    <th className="py-2 pr-3 font-medium text-right">Weight</th>
                    <th className="py-2 font-medium">Basis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200/70 dark:divide-slate-700/50">
                  {result.positions.map((p) => (
                    <tr key={p.symbol}>
                      <td className="py-2 pr-3 font-semibold text-slate-900 dark:text-white">{p.symbol}</td>
                      <td className="py-2 pr-3 text-right text-slate-600 dark:text-slate-300">{p.weightPct}%</td>
                      <td className="py-2 text-xs text-slate-500 dark:text-slate-400">{p.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 dark:text-slate-500">
            Simulations run locally with seeded randomness on top of the Marker Lab and Convertibles analytics.
            Distributions describe the model, not the market — research analytics for educational use, not
            investment advice.
          </p>
        </div>
      )}
    </ResearchShell>
  )
}
